import type { UpstreamRemoteSync, UpstreamRemoteSyncStatus } from "@istari/istari-client";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useContext, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { modelQueryKeys } from "~/api/model/modelQueryKeys";
import { getSyncStatusOptions, listRemotesOptions, triggerRemoteSync } from "~/api/remotes";
import { artifactQueryKeys } from "~/api/useModels";
import { RemotesOperationsContext, type RemotesOperationsValue } from "~/contexts/remotes-operations.context";

type TerminalStatus = Extract<UpstreamRemoteSyncStatus, "completed" | "failed" | "cancel">;

function useRemotesOperationsInternal(): RemotesOperationsValue {
  const queryClient = useQueryClient();
  const [selectedSources, setSelectedSources] = useState<string[]>([]);
  const [inProgressRemoteIds, setInProgressRemoteIds] = useState<string[]>([]);
  const [initiatingRemoteIds, setInitiatingRemoteIds] = useState<string[]>([]);

  const STORAGE_KEY = "remotes:lastResultByRemoteId";
  const [lastResultByRemoteId, setLastResultByRemoteId] = useState<Record<string, TerminalStatus>>(() => {
    try {
      if (typeof window === "undefined") return {};
      const raw = window.localStorage.getItem(STORAGE_KEY);
      return raw ? (JSON.parse(raw) as Record<string, TerminalStatus>) : {};
    } catch {
      return {};
    }
  });

  const syncIdToRemoteIdRef = useRef<Record<string, string>>({});

  const {
    data: availableRemotes = [],
    isLoading: isLoadingRemotes,
    isError: remotesError,
    error: remotesErrorDetails,
  } = useQuery(listRemotesOptions());

  useEffect(() => {
    if (remotesError) {
      console.error("Failed to load remotes:", remotesErrorDetails);
      toast.error("Failed to load sync sources");
    }
  }, [remotesError, remotesErrorDetails]);

  const syncAllMutation = useMutation({
    mutationFn: async () => {
      const ids = availableRemotes.map((r) => r.id);
      if (ids.length === 0) throw new Error("No sources available");
      const results = await Promise.allSettled(ids.map((id) => triggerRemoteSync(id)));
      const started = results
        .filter((r): r is PromiseFulfilledResult<UpstreamRemoteSync> => r.status === "fulfilled")
        .map((r) => r.value);
      const failed = results.length - started.length;
      if (started.length === 0) throw new Error("No sync could be started");

      started.forEach((s) => {
        syncIdToRemoteIdRef.current[s.id] = s.upstreamRemoteId;
      });
      setInProgressRemoteIds((prev) => {
        const set = new Set(prev);
        started.forEach((s) => set.add(s.upstreamRemoteId));
        return Array.from(set);
      });
      return { started, failed, ids };
    },
    onSuccess: ({ started, failed, ids }: { started: UpstreamRemoteSync[]; failed: number; ids: string[] }) => {
      setInitiatingRemoteIds((prev) => prev.filter((id) => !ids.includes(id)));
      if (failed > 0) toast.success(`Sync started for ${started.length} sources (${failed} failed to start)`);
      else toast.success("Sync started for all sources");
      queryClient.invalidateQueries({ queryKey: ["sync-status"] });
    },
    onError: (error: Error) => {
      setInitiatingRemoteIds([]);
      console.error("Failed to start sync:", error);
      toast.error("Failed to start sync");
    },
    meta: {
      skipGlobalInvalidation: true,
    },
  });

  const syncSelectedMutation = useMutation({
    mutationFn: async (remoteIds: string[]) => {
      if (remoteIds.length === 0) throw new Error("No sources selected");
      const results = await Promise.allSettled(remoteIds.map((id) => triggerRemoteSync(id)));
      const started = results
        .filter((r): r is PromiseFulfilledResult<UpstreamRemoteSync> => r.status === "fulfilled")
        .map((r) => r.value);
      const failed = results.length - started.length;
      if (started.length === 0) throw new Error("No sync could be started");

      started.forEach((s) => {
        syncIdToRemoteIdRef.current[s.id] = s.upstreamRemoteId;
      });
      setInProgressRemoteIds((prev) => {
        const set = new Set(prev);
        started.forEach((s) => set.add(s.upstreamRemoteId));
        return Array.from(set);
      });
      return { started, failed, remoteIds };
    },
    onSuccess: (
      { started, failed, remoteIds }: { started: UpstreamRemoteSync[]; failed: number; remoteIds: string[] },
      variables: string[],
    ) => {
      setInitiatingRemoteIds((prev) => prev.filter((id) => !remoteIds.includes(id)));
      const selectedNames = variables
        .map((id) => availableRemotes.find((r) => r.id === id)?.label)
        .filter(Boolean) as string[];
      if (failed > 0) {
        toast.success(`Sync started for ${started.length} sources (${failed} failed to start)`);
      } else {
        const message =
          selectedNames.length > 1
            ? `Sync started for ${selectedNames.length} sources`
            : `Sync started for ${selectedNames[0]}`;
        toast.success(message);
      }
      queryClient.invalidateQueries({ queryKey: ["sync-status"] });
    },
    onError: (error: Error, variables) => {
      setInitiatingRemoteIds((prev) => prev.filter((id) => !variables.includes(id)));
      console.error("Failed to start sync:", error);
      toast.error("Failed to start sync for selected sources");
    },
    meta: {
      skipGlobalInvalidation: true,
    },
  });

  const syncStatusQueries = useQueries({
    queries: Object.keys(syncIdToRemoteIdRef.current).map((syncId) => ({
      ...getSyncStatusOptions(syncId),
      enabled: true,
      refetchInterval: (query: { state: { data?: UpstreamRemoteSync } }) => {
        const status = query.state.data;
        if (status?.status === "completed" || status?.status === "failed" || status?.status === "cancel") {
          return false;
        }
        return 2000;
      },
    })),
  });

  const processedSyncIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    syncStatusQueries.forEach((query) => {
      const data = query.data;
      if (
        data &&
        data.status !== "in_progress" &&
        data.status !== "pending" &&
        !processedSyncIdsRef.current.has(data.id)
      ) {
        processedSyncIdsRef.current.add(data.id);
        const remoteId = syncIdToRemoteIdRef.current[data.id];
        if (remoteId) {
          setInProgressRemoteIds((prev) => {
            const filtered = prev.filter((id) => id !== remoteId);
            return filtered;
          });
          setLastResultByRemoteId((prev) => {
            const next = { ...prev, [remoteId]: data.status as TerminalStatus };
            try {
              if (typeof window !== "undefined") window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
            } catch {}
            return next;
          });
          delete syncIdToRemoteIdRef.current[data.id];
          if (data.status === "completed") {
            toast.success(`Sync completed successfully`);
            queryClient.invalidateQueries({ queryKey: modelQueryKeys.all });
            queryClient.invalidateQueries({ queryKey: artifactQueryKeys.all });
          } else if (data.status === "failed" || data.status === "cancel") {
            toast.error(`Sync failed: ${data.message}`);
          }
        }
      }
    });
  }, [syncStatusQueries, queryClient]); // Dependencies required to prevent flushSync error from running effect on every render

  const toggleSource = (sourceId: string) => {
    setSelectedSources((prev) =>
      prev.includes(sourceId) ? prev.filter((id) => id !== sourceId) : [...prev, sourceId],
    );
  };
  const selectAllSources = () => setSelectedSources(availableRemotes.map((remote) => remote.id));
  const clearAllSources = () => setSelectedSources([]);
  const isAllSourcesSelected = selectedSources.length === availableRemotes.length && availableRemotes.length > 0;

  const syncAll = () => {
    if (availableRemotes.length === 0) {
      toast.error("No sync sources available");
      return;
    }
    setInitiatingRemoteIds(availableRemotes.map((r) => r.id));
    syncAllMutation.mutate();
  };
  const syncSelected = () => {
    if (selectedSources.length === 0) {
      toast.error("Please select at least one source to sync");
      return;
    }
    syncSelectedMutation.mutate(selectedSources);
  };

  const syncRemote = (remoteId: string) => {
    setInitiatingRemoteIds((prev) => [...prev, remoteId]);
    syncSelectedMutation.mutate([remoteId]);
  };

  const isInitiatingSyncAll = syncAllMutation.isPending;
  const isSyncing = inProgressRemoteIds.length > 0;

  return {
    availableRemotes,
    isLoadingRemotes,
    selectedSources,
    toggleSource,
    selectAllSources,
    clearAllSources,
    isAllSourcesSelected,
    syncAll,
    syncSelected,
    syncRemote,
    isInitiatingSyncAll,
    isSyncing,
    inProgressRemoteIds,
    initiatingRemoteIds,
    lastResultByRemoteId,
  };
}

export function useRemotesOperations(): RemotesOperationsValue {
  const ctx = useContext(RemotesOperationsContext);
  if (!ctx) {
    throw new Error("useRemotesOperations must be used within RemotesOperationsProvider");
  }
  return ctx;
}

export function RemotesOperationsProviderAdapter({ children }: { children: React.ReactNode }) {
  const value = useRemotesOperationsInternal();
  return React.createElement(RemotesOperationsContext.Provider, { value }, children as React.ReactNode);
}
